"use client";

// ============================================================
// CLUBHUB – Nachrichten: Zone 3, wie es ankommt
// ============================================================
// Die echte Vorschau je Empfaenger: durchblaettern, Platzhalter
// eingesetzt, und zwar auf dem Weg, auf dem die Person die Nachricht
// wirklich bekommt — wer bei einer Mail Post wuenscht, sieht hier
// seinen Brief. Bildschirm oder Telefon fuer die Mail, Zeichen-
// zaehler fuer die SMS, Hinweise zu leeren Platzhaltern (ueber dem
// Blatt, damit sie nicht unter einer langen Mail verschwinden).
// ============================================================
import { ChevronLeft, ChevronRight, Monitor, Smartphone, TriangleAlert } from "lucide-react";
import type { MailingChannel } from "@/generated/prisma/enums";
import { PLAETTCHEN, PLAETTCHEN_AN, PLAETTCHEN_AUS, Schiene } from "@/components/liste";
import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { BRIEFGRUND, type Empfaenger } from "@/lib/nachrichten/empfaenger";
import {
  anschriftZeilen,
  einsetzen,
  leereWerte,
  PLATZHALTER,
  unbekannte,
  werteFuer,
} from "@/lib/nachrichten/platzhalter";
import type { AnhangView, VereinsangabenView, Zustellweg, Zweck } from "@/lib/nachrichten/views";
import type { ContactView, FeeTypeView } from "@/lib/views";
import { BriefVorschau, MailVorschau, SmsVorschau } from "./vorschau";
import { WEG_LABEL } from "./kanal";
import { Hinweis } from "./zone";

const DATUM_LANG = new Intl.DateTimeFormat("de-DE", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

export function VorschauZone({
  channel,
  zweck,
  subject,
  body,
  fromName,
  replyTo,
  attachments,
  personen,
  index,
  onIndex,
  geraet,
  onGeraet,
  verein,
  beitraege,
  musterperson,
}: {
  channel: MailingChannel;
  zweck: Zweck;
  subject: string;
  body: string;
  fromName: string;
  replyTo: string | null;
  attachments: AnhangView[];
  /** Die Empfaenger, durch die man blaettert (mit ihrem Weg). */
  personen: Empfaenger[];
  index: number;
  onIndex: (i: number) => void;
  geraet: "desktop" | "telefon";
  onGeraet: (g: "desktop" | "telefon") => void;
  verein: VereinsangabenView;
  beitraege: FeeTypeView[];
  /** Fuer die Vorschau, solange noch niemand gewaehlt ist. */
  musterperson: ContactView | undefined;
}) {
  const i = Math.min(index, Math.max(0, personen.length - 1));
  const aktuell = personen[i];
  const kontakt = aktuell?.kontakt ?? musterperson;
  const weg: Zustellweg =
    aktuell?.weg ?? (channel === "SMS" ? "SMS" : channel === "LETTER" ? "LETTER" : "EMAIL");
  const ctx = { verein: verein.name, beitraege };
  const werte = kontakt
    ? werteFuer(kontakt, ctx)
    : Object.fromEntries(PLATZHALTER.map((p) => [p.schluessel, `{{${p.schluessel}}}`]));
  const betreff = einsetzen(subject, werte);
  const text = einsetzen(body, werte);

  const leer = leereWerte(
    `${subject}\n${body}`,
    personen.map((p) => p.kontakt),
    ctx,
  );
  const falsch = unbekannte(`${subject}\n${body}`);

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex min-w-0 flex-1 items-center gap-1">
          <button
            type="button"
            onClick={() => onIndex(Math.max(0, i - 1))}
            disabled={i === 0 || personen.length === 0}
            aria-label="Vorige Person"
            className="text-text-secondary hover:bg-bg-tertiary grid h-9 w-9 shrink-0 place-items-center rounded-lg disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </button>
          <Select
            wrapperClassName="min-w-0 flex-1"
            options={personen.map((p) => ({
              value: p.kontakt.id,
              label: p.kontakt.displayName,
              hint: `${WEG_LABEL[p.weg!]}${p.briefgrund ? ` · ${BRIEFGRUND[p.briefgrund]}` : ""}`,
            }))}
            value={aktuell?.kontakt.id ?? ""}
            onChange={(id) =>
              onIndex(
                Math.max(
                  0,
                  personen.findIndex((p) => p.kontakt.id === id),
                ),
              )
            }
            placeholder={
              musterperson ? `Musterperson: ${musterperson.displayName}` : "Noch keine Empfänger"
            }
            disabled={personen.length === 0}
            searchable
            aria-label="Vorschau für"
          />
          <button
            type="button"
            onClick={() => onIndex(Math.min(personen.length - 1, i + 1))}
            disabled={i >= personen.length - 1}
            aria-label="Nächste Person"
            className="text-text-secondary hover:bg-bg-tertiary grid h-9 w-9 shrink-0 place-items-center rounded-lg disabled:opacity-40"
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
          </button>
        </div>
        {weg === "EMAIL" && (
          <Schiene>
            {(
              [
                ["desktop", "Bildschirm", Monitor],
                ["telefon", "Telefon", Smartphone],
              ] as const
            ).map(([wert, label, Icon]) => (
              <button
                key={wert}
                type="button"
                aria-pressed={geraet === wert}
                onClick={() => onGeraet(wert)}
                title={label}
                className={cn(
                  PLAETTCHEN,
                  "text-body-sm gap-1.5 px-2.5",
                  geraet === wert ? cn(PLAETTCHEN_AN, "font-medium") : PLAETTCHEN_AUS,
                )}
              >
                <Icon className="h-3.5 w-3.5" aria-hidden />
                <span className="max-sm:sr-only">{label}</span>
              </button>
            ))}
          </Schiene>
        )}
      </div>

      <p className="text-caption text-text-tertiary numeric -mt-2">
        {personen.length > 0 ? (
          <>
            {i + 1} von {personen.length} · bekommt{" "}
            {weg === "EMAIL" ? "eine Mail" : weg === "SMS" ? "eine SMS" : "einen Brief"}
            {aktuell?.briefgrund && ` (${BRIEFGRUND[aktuell.briefgrund]})`}
          </>
        ) : (
          "Noch keine Empfänger gewählt — die Vorschau zeigt eine Musterperson."
        )}
      </p>

      {(falsch.length > 0 || leer.length > 0) && (
        <div className="space-y-2">
          {falsch.length > 0 && (
            <Hinweis
              ton="danger"
              icon={<TriangleAlert className="text-danger h-4 w-4" aria-hidden />}
            >
              Unbekannter Platzhalter: {falsch.join(", ")} — er bliebe so im Text stehen.
            </Hinweis>
          )}
          {leer.map((l) => (
            <Hinweis
              key={l.schluessel}
              ton="warning"
              icon={<TriangleAlert className="text-warning h-4 w-4" aria-hidden />}
            >
              <span className="font-mono">{`{{${l.schluessel}}}`}</span> bleibt bei{" "}
              <strong className="numeric font-medium">{l.anzahl}</strong>{" "}
              {l.anzahl === 1 ? "Empfänger" : "Empfängern"} leer.
            </Hinweis>
          ))}
        </div>
      )}
      {/* Die graue Flaeche reicht bis zum Kartenende; das Blatt darauf
          bleibt bei langem Text neben der Schreibstelle (ab xl). */}
      <div className="bg-bg-secondary -mx-5 -mb-5 flex-1 rounded-b-xl px-3 py-5 sm:px-5">
        <div className="xl:sticky xl:top-[calc(var(--ans-topbar)+20px)]">
          {weg === "EMAIL" && (
            <MailVorschau
              verein={verein}
              von={fromName || verein.absenderName}
              antwortAn={replyTo}
              an={
                kontakt
                  ? `${kontakt.displayName.includes(",") ? `${kontakt.firstName} ${kontakt.lastName}` : kontakt.displayName} <${kontakt.email ?? "keine Adresse"}>`
                  : "Empfänger"
              }
              betreff={betreff}
              text={text}
              zweck={zweck}
              anhaenge={attachments}
              geraet={geraet}
            />
          )}
          {weg === "SMS" && <SmsVorschau verein={verein} text={text} />}
          {weg === "LETTER" && (
            <BriefVorschau
              verein={verein}
              empfaenger={kontakt ? anschriftZeilen(kontakt) : ["Anschrift"]}
              betreff={betreff}
              text={text}
              absender={fromName || verein.absenderName}
              datum={DATUM_LANG.format(new Date())}
              anhaenge={attachments}
            />
          )}
        </div>
      </div>
    </>
  );
}
